<template>
    <Dialog v-model:open="isOpen">
        <DialogContent id="notification" class="md:max-w-4xl">
            <DialogTitle class="text-xl">
                {{ $t("Notifications.ProductNotification") }}
            </DialogTitle>
            <div class="flex flex-col md:flex-row border-b border-gray-100 gap-1">
                <div class="md:w-1/12 text-left">
                    <div class="max-w-[65px]">
                        <div v-if="!part.ImageURL">
                            <a v-if="part.PartDetailURL" :href="part.PartDetailURL">
                                <i class="fass fa-image" />
                            </a>
                            <i v-else class="fass fa-image" />
                        </div>
                        <a v-if="part.PartDetailURL" :href="part.PartDetailURL">
                            <img
                                v-if="part.ImageURL"
                                :src="part.ImageURL"
                                :alt="part.PartNumber"
                                :title="part.PartNumber" />
                        </a>
                        <img
                            v-else-if="part.ImageURL"
                            :src="part.ImageURL"
                            :alt="part.PartNumber"
                            :title="part.PartNumber" />
                    </div>
                </div>
                <div class="md:w-1/2">
                    <h4 class="mt-0 mb-6 space-y-1">
                        <div class="text-sm text-gray-500">{{ part.Manufacturer }}</div>
                        <div>
                            <strong>{{ part.PartNumber }}</strong>
                        </div>
                        <p>
                            <button class="text-sm link-blue" @click="logManageAlertsLink">
                                {{ $t("Notifications.ManageAlertsLink") }}
                            </button>
                        </p>
                    </h4>
                </div>
            </div>

            <div v-show="error" class="alert alert-danger">{{ error }}</div>

            <p>{{ $t("Notifications.PartNotificationMessage") }}</p>

            <div class="panel mb-4">
                <div class="panel-heading" @click="isStockToggleOn = !isStockToggleOn">
                    <div class="flex items-center justify-between py-1">
                        <div class="flex grow items-center gap-2">
                            <i v-if="isStockToggleOn" class="fass fa-chevron-down" />
                            <i v-else class="fass fa-chevron-up" />
                            <h4 class="font-bold">
                                {{ getTitleCount(stockAlerts, "stockAlert") }}
                            </h4>
                            <a :href="popupHelpLink" target="_blank">
                                <i class="fas fa-circle-question" />
                            </a>
                        </div>
                        <button type="button" class="btn btn-bg-primary" @click="openNewStockAlert">
                            <i class="fas fa-plus text-xs" />
                            <span>{{ $t("Global.Add") }}</span>
                        </button>
                    </div>
                </div>
                <!-- StockAlert -->
                <div :class="{ hidden: !isStockToggleOn }">
                    <div class="panel-body">
                        <table v-if="stockAlerts.length || addStockAlertState" class="table table-alerts">
                            <thead>
                                <tr>
                                    <th class="text-left">{{ $t("Global.Quantity") }}</th>
                                    <th class="text-left">{{ $t("Notifications.AboveOrBelow") }}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="addStockAlertState">
                                    <td>
                                        <div class="" :class="{ 'has-error': newStockAlert.errors.Quantity }">
                                            <input
                                                v-model.number="newStockAlert.Quantity"
                                                class="table-alerts-input"
                                                type="number"
                                                step="10"
                                                min="0"
                                                :disabled="saving"
                                                @keydown="validateNumber($event)" />
                                        </div>
                                        <span class="text-danger">{{ newStockAlert.errors.Quantity }}</span>
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            :class="{
                                                'btn-bg-primary': newStockAlert.Modifier == MODIFIER_TYPE.GREATER_THAN,
                                            }"
                                            class="btn btn-xs"
                                            @click="selectGreaterThan(newStockAlert)">
                                            <i class="fas fa-caret-up" />
                                        </button>
                                        <button
                                            type="button"
                                            :class="{
                                                'btn-bg-primary': newStockAlert.Modifier == MODIFIER_TYPE.LESS_THAN,
                                            }"
                                            class="btn btn-xs"
                                            @click="selectLessThan(newStockAlert)">
                                            <i class="fas fa-caret-down" />
                                        </button>
                                    </td>
                                    <td class="text-end">
                                        <button
                                            type="button"
                                            class="btn btn-primary rounded-full"
                                            :title="$t('Notifications.Save')"
                                            @click="addStockAlert">
                                            {{ $t("Notifications.Save") }}
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-danger rounded-full"
                                            :disabled="saving"
                                            :title="$t('Global.Cancel')"
                                            @click="addStockAlertState = false">
                                            {{ $t("Global.Cancel") }}
                                        </button>
                                    </td>
                                </tr>
                                <tr v-for="stockAlert in stockAlerts" :key="stockAlert.Id">
                                    <td>
                                        <span v-show="!stockAlert.EditMode">{{ stockAlert.QuantityFormatted }}</span>
                                        <div
                                            v-show="stockAlert.EditMode"
                                            class=""
                                            v:class="{ 'has-error': stockAlert.errors.Quantity }">
                                            <input
                                                v-model.number="stockAlert.Quantity"
                                                class="table-alerts-input"
                                                type="number"
                                                step="10"
                                                min="0"
                                                :disabled="saving"
                                                @keydown="validateNumber($event)"
                                                @change="validateStockAlert(stockAlert)" />
                                            <span class="text-danger">{{ stockAlert.errors.Quantity }}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <button
                                            v-show="
                                                !stockAlert.EditMode &&
                                                stockAlert.Modifier == MODIFIER_TYPE.GREATER_THAN
                                            "
                                            type="button"
                                            class="btn btn-xs btn-bg-primary">
                                            <i class="fas fa-caret-up" />
                                        </button>
                                        <button
                                            v-show="
                                                !stockAlert.EditMode && stockAlert.Modifier == MODIFIER_TYPE.LESS_THAN
                                            "
                                            type="button"
                                            class="btn btn-xs btn-bg-primary">
                                            <i class="fas fa-caret-down" />
                                        </button>
                                        <button
                                            v-show="stockAlert.EditMode"
                                            type="button"
                                            class="btn btn-xs"
                                            :class="{
                                                'btn-bg-primary': stockAlert.Modifier == MODIFIER_TYPE.GREATER_THAN,
                                            }"
                                            :disabled="saving"
                                            @click="selectGreaterThan(stockAlert)">
                                            <i class="fas fa-caret-up" />
                                        </button>
                                        <button
                                            v-show="stockAlert.EditMode"
                                            type="button"
                                            class="btn btn-xs"
                                            :class="{
                                                'btn-bg-primary': stockAlert.Modifier == MODIFIER_TYPE.LESS_THAN,
                                            }"
                                            :disabled="saving"
                                            @click="selectLessThan(stockAlert)">
                                            <i class="fas fa-caret-down" />
                                        </button>
                                    </td>
                                    <td class="text-right">
                                        <button
                                            v-show="!stockAlert.EditMode"
                                            type="button"
                                            class="btn btn-primary rounded-full"
                                            :title="$t('Global.Edit')"
                                            :disabled="stockAlert.Status == alertStatuses.DELETED"
                                            @click="setEditMode(stockAlert)">
                                            {{ $t("Global.Edit") }}
                                        </button>
                                        <button
                                            v-show="!stockAlert.EditMode"
                                            type="button"
                                            class="btn btn-danger rounded-full"
                                            :title="$t('Global.Delete')"
                                            :disabled="stockAlert.Status == alertStatuses.DELETED"
                                            @click="deleteAlert(stockAlert)">
                                            {{ $t("Global.Delete") }}
                                        </button>
                                        <button
                                            v-show="stockAlert.EditMode"
                                            type="button"
                                            class="btn btn-primary rounded-full"
                                            :title="$t('Global.Confirm')"
                                            :disabled="saving"
                                            @click="saveStockAlert(stockAlert)">
                                            <i v-show="saving" class="fass fa-arrows-rotate animate-spin" />
                                            {{ $t("Global.Confirm") }}
                                        </button>
                                        <button
                                            v-show="stockAlert.EditMode"
                                            type="button"
                                            class="btn btn-danger rounded-full"
                                            :disabled="saving"
                                            :title="$t('Global.Cancel')"
                                            @click="cancelEditMode(stockAlert)">
                                            {{ $t("Global.Cancel") }}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-else class="p-3">
                            <em>{{ $t("Notifications.AddAlertMessage") }}</em>
                        </div>
                    </div>
                </div>
            </div>

            <div class="panel">
                <div class="panel-heading" @click="isPriceToggleOn = !isPriceToggleOn">
                    <div class="flex items-center justify-between py-1">
                        <div class="flex grow items-center gap-2">
                            <i v-if="isPriceToggleOn" class="fass fa-chevron-down" />
                            <i v-else class="fass fa-chevron-up" />
                            <h4 class="font-bold">
                                {{ getTitleCount(priceAlerts, "priceAlert") }}
                            </h4>
                            <a :href="popupHelpLink" target="_blank">
                                <i class="fas fa-circle-question" />
                            </a>
                        </div>
                        <button type="button" class="btn btn-bg-primary" @click="openNewPriceAlert">
                            <i class="fas fa-plus text-xs mr-1" />
                            {{ $t("Global.Add") }}
                        </button>
                    </div>
                </div>
                <!-- PriceAlert -->
                <div :class="{ hidden: !isPriceToggleOn }">
                    <div class="panel-body">
                        <table v-if="priceAlerts.length || addPriceAlertState" class="table table-alerts">
                            <thead>
                                <tr>
                                    <th class="text-left">{{ $t("Global.Quantity") }}</th>
                                    <th class="text-left">{{ $t("Notifications.IncreaseOrDecrease") }}</th>
                                    <th class="text-left">{{ $t("Notifications.Amount") }}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="addPriceAlertState">
                                    <td>
                                        <div class="" :class="{ 'has-error': newPriceAlert.errors.Quantity }">
                                            <input
                                                v-model.number="newPriceAlert.Quantity"
                                                class="table-alerts-input"
                                                type="number"
                                                step="10"
                                                min="1"
                                                :disabled="saving"
                                                @keydown="validateNumber($event)" />
                                        </div>
                                        <span class="text-danger">{{ newPriceAlert.errors.Quantity }}</span>
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            :class="{
                                                'btn-bg-primary': newPriceAlert.Modifier == MODIFIER_TYPE.GREATER_THAN,
                                            }"
                                            class="btn btn-xs"
                                            @click="selectGreaterThan(newPriceAlert)">
                                            <i class="fas fa-caret-up" />
                                        </button>
                                        <button
                                            type="button"
                                            :class="{
                                                'btn-bg-primary': newPriceAlert.Modifier == MODIFIER_TYPE.LESS_THAN,
                                            }"
                                            class="btn btn-xs"
                                            @click="selectLessThan(newPriceAlert)">
                                            <i class="fas fa-caret-down" />
                                        </button>
                                    </td>
                                    <td>
                                        <div class="" :class="{ 'has-error': newPriceAlert.errors.Amount }">
                                            <div class="input-group input-group-with-button">
                                                <input v-model="newPriceAlert.Amount" type="number" min="1" max="100" />
                                                <span class="input-group-after btn hover:bg-white">%</span>
                                            </div>
                                            <span class="text-danger">{{ newPriceAlert.errors.Amount }}</span>
                                        </div>
                                    </td>
                                    <td class="text-end">
                                        <button
                                            type="button"
                                            class="btn btn-primary rounded-full"
                                            :title="$t('Notifications.Save')"
                                            @click="addPriceAlert">
                                            {{ $t("Notifications.Save") }}
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-danger rounded-full"
                                            :disabled="saving"
                                            :title="$t('Global.Cancel')"
                                            @click="addPriceAlertState = false">
                                            {{ $t("Global.Cancel") }}
                                        </button>
                                    </td>
                                </tr>
                                <tr v-for="priceAlert in priceAlerts" :key="priceAlert.Id">
                                    <td>
                                        <span v-show="!priceAlert.EditMode">{{ priceAlert.QuantityFormatted }}</span>
                                        <div
                                            v-show="priceAlert.EditMode"
                                            class=""
                                            :class="{ 'has-error': priceAlert.errors.Quantity }">
                                            <input
                                                v-model.number="priceAlert.Quantity"
                                                class="table-alerts-input"
                                                type="number"
                                                step="10"
                                                min="1"
                                                :disabled="saving"
                                                @keydown="validateNumber($event)" />
                                            <span class="text-danger">{{ priceAlert.errors.Quantity }}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <button
                                            v-show="
                                                !priceAlert.EditMode &&
                                                priceAlert.Modifier == MODIFIER_TYPE.GREATER_THAN
                                            "
                                            type="button"
                                            class="btn btn-xs btn-bg-primary">
                                            <i class="fas fa-caret-up" />
                                        </button>
                                        <button
                                            v-show="
                                                !priceAlert.EditMode && priceAlert.Modifier == MODIFIER_TYPE.LESS_THAN
                                            "
                                            type="button"
                                            class="btn btn-xs btn-bg-primary">
                                            <i class="fas fa-caret-down" />
                                        </button>
                                        <button
                                            v-show="priceAlert.EditMode"
                                            type="button"
                                            class="btn btn-xs"
                                            :class="{
                                                'btn-bg-primary': priceAlert.Modifier == MODIFIER_TYPE.GREATER_THAN,
                                            }"
                                            :disabled="saving"
                                            @click="selectGreaterThan(priceAlert)">
                                            <i class="fas fa-caret-up" />
                                        </button>
                                        <button
                                            v-show="priceAlert.EditMode"
                                            type="button"
                                            class="btn btn-xs"
                                            :class="{
                                                'btn-bg-primary': priceAlert.Modifier == MODIFIER_TYPE.LESS_THAN,
                                            }"
                                            :disabled="saving"
                                            @click="selectLessThan(priceAlert)">
                                            <i class="fas fa-caret-down" />
                                        </button>
                                    </td>
                                    <td>
                                        <div
                                            v-show="priceAlert.EditMode"
                                            :class="{ 'text-error': priceAlert.errors.Amount }">
                                            <div class="input-group input-group-with-button">
                                                <input
                                                    v-model="priceAlert.Amount"
                                                    type="number"
                                                    min="1"
                                                    max="100"
                                                    :disabled="saving"
                                                    @change="validatePriceAlert(priceAlert)" />
                                                <span class="input-group-after btn hover:bg-white">%</span>
                                            </div>
                                            <span class="text-danger">{{ priceAlert.errors.Amount }}</span>
                                        </div>
                                        <span v-show="!priceAlert.EditMode">{{ priceAlert.Amount }}%</span>
                                    </td>
                                    <td class="text-right">
                                        <button
                                            v-show="!priceAlert.EditMode"
                                            type="button"
                                            class="btn btn-primary rounded-full"
                                            :title="$t('Global.Edit')"
                                            :disabled="priceAlert.Status == alertStatuses.DELETED"
                                            @click="setEditMode(priceAlert)">
                                            {{ $t("Global.Edit") }}
                                        </button>
                                        <button
                                            v-show="!priceAlert.EditMode"
                                            type="button"
                                            class="btn btn-danger rounded-full"
                                            :title="$t('Global.Delete')"
                                            :disabled="priceAlert.Status == alertStatuses.DELETED"
                                            @click="deleteAlert(priceAlert)">
                                            {{ $t("Global.Delete") }}
                                        </button>
                                        <button
                                            v-show="priceAlert.EditMode"
                                            type="button"
                                            class="btn btn-primary rounded-full"
                                            :title="$t('Global.Confirm')"
                                            :disabled="saving"
                                            @click="savePriceAlert(priceAlert)">
                                            <i
                                                v-show="saving"
                                                style="font-size: 13px"
                                                class="fass fa-arrows-rotate animate-spin" />
                                            {{ $t("Global.Confirm") }}
                                        </button>
                                        <button
                                            v-show="priceAlert.EditMode"
                                            type="button"
                                            class="btn btn-danger rounded-full"
                                            :disabled="saving"
                                            :title="$t('Global.Cancel')"
                                            @click="cancelEditMode(priceAlert)">
                                            {{ $t("Global.Cancel") }}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <div v-else class="p-3">
                            <em>{{ $t("Notifications.AddAlertMessage") }}</em>
                        </div>
                    </div>
                </div>
            </div>
        </DialogContent>
    </Dialog>
</template>

<script setup>
const { gtag } = useGtag();
const { t } = useI18n();
const isOpen = useStateNotificationIsOpen();

const props = defineProps({
    part: { type: Object, required: true },
    notification: { type: Object, default: null },
    modalId: { type: String, default: null },
});

const emit = defineEmits(["open", "close", "update"]);

const addStockAlertState = ref(false);
const addPriceAlertState = ref(false);
const isStockToggleOn = ref(true);
const isPriceToggleOn = ref(true);
const saving = ref(false);
const error = ref("");
const record = ref({
    StockAlerts: [],
    PriceAlerts: [],
    PartId: props.part.Id,
});
const newStockAlert = ref({
    Modifier: MODIFIER_TYPE.GREATER_THAN, // Default value
    errors: {},
});
const newPriceAlert = ref({
    Modifier: MODIFIER_TYPE.GREATER_THAN, // Default value
    errors: {},
});
const alertStatuses = {
    ENABLED: 1,
    DISABLED: 2,
    DELETED: 3,
};

const popupHelpLink = computed(() => {
    return useDocsUrl("/stock-and-price-alerts.html");
});

const stockAlerts = computed(() => {
    return record.value.StockAlerts.filter((x) => x.Status == alertStatuses.ENABLED);
});

const priceAlerts = computed(() => {
    return record.value.PriceAlerts.filter((x) => x.Status == alertStatuses.ENABLED);
});

watch(isOpen, () => {
    if (isOpen.value) {
        createRecord();

        gtag("event", "Open Stock and Price Alerts Modal", {
            event_category: "Stock and Price Alerts",
            event_label: props.part.PartNumber,
        });
        gtag("event", "alerts_open_modal", { part_number: props.part.PartNumber });
        emit("open");
    } else {
        // reset the record if the user has unsaved changes and closes the modal.
        const resetAlert = (alert) => {
            if (alert.EditMode) {
                // reset the props from the original json object.
                Object.assign(alert, JSON.parse(alert.original));
                alert.EditMode = false;
            }
        };

        for (const alert of record.value.StockAlerts) {
            resetAlert(alert);
        }

        for (const alert of record.value.PriceAlerts) {
            resetAlert(alert);
        }
        emit("close");
    }
});

function createRecord() {
    record.value = props.notification;
    if (!record.value || !record.value.Id) {
        // set defaults
        record.value = {
            StockAlerts: [],
            PriceAlerts: [],
            PartId: props.part.Id,
        };
    }

    // Grab the whole part so that we can get part data from ES.
    // Create a copy of the part record with only the information we actually need.
    record.value.Part = {
        Id: props.part.Id,
        PartNumber: props.part.PartNumber,
        PartNumberScrubbed: props.part.PartNumberScrubbedNonMeaningful || props.part.PartNumberScrubbed,
        Manufacturer: props.part.Manufacturer || props.part.ManufacturerName,
        ManufacturerId: props.part.ManufacturerId,
    };

    resetErrors();
}

function selectGreaterThan(alert) {
    alert.Modifier = MODIFIER_TYPE.GREATER_THAN;
}

function selectLessThan(alert) {
    alert.Modifier = MODIFIER_TYPE.LESS_THAN;
}

function getTitleCount(notifications, alertType) {
    return alertType == "stockAlert"
        ? t("Notifications.StockAlertsCount", [notifications.length])
        : t("Notifications.PriceAlertsCount", [notifications.length]);
}

function addStockAlert() {
    newStockAlert.value.Status = 1;
    newStockAlert.value.EditMode = false;

    if (!validateStockAlert(newStockAlert.value)) {
        return;
    }

    record.value.StockAlerts.push(newStockAlert.value);
    newStockAlert.value = {
        errors: {},
    };
    addStockAlertState.value = false;

    save();
}

function addPriceAlert() {
    newPriceAlert.value.Status = 1;
    newPriceAlert.value.EditMode = false;

    if (!validatePriceAlert(newPriceAlert.value)) {
        return;
    }

    record.value.PriceAlerts.push(newPriceAlert.value);
    newPriceAlert.value = {
        errors: {},
    };
    addPriceAlertState.value = false;

    save();
}

function deleteAlert(alert) {
    gtag("event", "Delete Alert", { event_category: "Stock and Price Alerts" });
    gtag("event", "alerts_delete", { part_number: props.part.PartNumber });

    alert.Status = 3; // deleted
    save();
}

function validateNumber(e) {
    // prevent 'e' and '.' (Period and NumpadDecimal), which are permitted in number type
    if (e.keyCode === 69 || e.keyCode === 190 || e.keyCode === 110) {
        e.preventDefault();
    }
}

function validateStockAlert(alert) {
    const errors = {};

    if (alert.Quantity == null || alert.Quantity < 0) {
        errors.Quantity = t("Notifications.AlertInvalidStockQuantity");
    }

    if (
        alert.Modifier == null ||
        (alert.Modifier != MODIFIER_TYPE.GREATER_THAN && alert.Modifier != MODIFIER_TYPE.LESS_THAN)
    ) {
        errors.Quantity = t("Notifications.AlertInvalidStockQuantity");
    }

    alert.errors = errors;

    if (Object.keys(errors).length > 0) {
        return false;
    }

    return true;
}

function validatePriceAlert(alert) {
    const errors = {};

    if (!alert.Quantity || alert.Quantity < 1) {
        errors.Quantity = t("Notifications.AlertInvalidQuantity");
    }

    if (!alert.Amount || alert.Amount <= 0 || alert.Amount > 100) {
        errors.Amount = t("Notifications.AlertInvalidAmount");
    }

    if (!alert.Status) {
        errors.Status = t("Notifications.AlertInvalidStatus");
    }

    if (
        alert.Modifier == null ||
        (alert.Modifier != MODIFIER_TYPE.GREATER_THAN && alert.Modifier != MODIFIER_TYPE.LESS_THAN)
    ) {
        errors.Quantity = t("Notifications.AlertInvalidStockQuantity");
    }

    alert.errors = errors;

    if (Object.keys(errors).length > 0) {
        return false;
    }

    return true;
}

function setEditMode(alert) {
    gtag("event", "Edit Alert", { event_category: "Stock and Price Alerts" });
    gtag("event", "alerts_edit", { part_number: props.part.PartNumber });

    alert.EditMode = true;
    // store a copy of the original record, in case the user wants to cancel.
    alert.original = JSON.stringify(alert);
}

function cancelEditMode(alert) {
    gtag("event", "Cancel Edit Alert", { event_category: "Stock and Price Alerts" });

    // reset the props from the original json object.
    Object.assign(alert, JSON.parse(alert.original));
    alert.EditMode = false;
}

function saveStockAlert(alert) {
    if (!validateStockAlert(alert)) {
        return;
    }

    gtag("event", "Save Stock Alert", { event_category: "Stock and Price Alerts" });
    gtag("event", "alerts_save_stock", { part_number: props.part.PartNumber });

    save(alert);
}

function savePriceAlert(alert) {
    if (!validatePriceAlert(alert)) {
        return;
    }

    gtag("event", "Save Price Alert", { event_category: "Stock and Price Alerts" });
    gtag("event", "alerts_save_price", { part_number: props.part.PartNumber });

    save(alert);
}

async function save(alert) {
    // double check we have no errors
    for (const alert of record.value.StockAlerts) {
        if (Object.keys(alert.errors).length > 0) {
            return;
        }
    }

    for (const alert of record.value.PriceAlerts) {
        if (Object.keys(alert.errors).length > 0) {
            return;
        }
    }

    saving.value = true;
    error.value = "";

    try {
        // TODO: seems previous version has sometimes record.Part end up being null. see if this still happens
        const response = await axios({
            method: record.value.Id ? "PUT" : "POST",
            url: "/api/part-notifications",
            data: record.value,
            responseType: "json",
        });
        if (response.data.error) {
            error.value = response.data.error;
        } else {
            record.value = response.data;
            resetErrors();
        }
    } catch (ex) {
        error.value = t("Notifications.NotificationError");
        console.error(ex);
    }

    saving.value = false;

    if (alert && !error.value) {
        alert.EditMode = false;
    }

    emit("update", record.value);
}

function resetErrors() {
    for (const alert of record.value.StockAlerts) {
        alert.errors = {};
    }

    for (const alert of record.value.PriceAlerts) {
        alert.errors = {};
    }

    error.value = "";
}

function openNewPriceAlert(event) {
    // button inside clickeable div
    event.stopPropagation();

    if (isPriceToggleOn.value == false) {
        isPriceToggleOn.value = true;
    }

    gtag("event", "Add Price Alert", { event_category: "Stock and Price Alerts" });
    gtag("event", "alerts_add_price", { part_number: props.part.PartNumber });

    addPriceAlertState.value = true;

    // default to 10%;
    newPriceAlert.value.Amount = 10;
    // default to >
    newPriceAlert.value.Modifier = MODIFIER_TYPE.GREATER_THAN;
}

function openNewStockAlert(event) {
    // button inside clickeable div
    event.stopPropagation();

    if (isStockToggleOn.value == false) {
        isStockToggleOn.value = true;
    }

    gtag("event", "Add Stock Alert", { event_category: "Stock and Price Alerts" });
    gtag("event", "alerts_add_stock", { part_number: props.part.PartNumber });

    // default to >
    newStockAlert.value.Modifier = MODIFIER_TYPE.GREATER_THAN;

    addStockAlertState.value = true;
}

function logManageAlertsLink(event) {
    event.stopPropagation();

    gtag("event", "Manage Alerts", {
        event_category: "Stock and Price Alerts",
        event_label: props.part.PartNumber,
    });
    gtag("event", "alerts_manage", { part_number: props.part.PartNumber });
    window.location.href = "/my-account#notifications";
}
</script>
<style>
.table-alerts tbody tr {
    @apply border-t border-gray-100;
}
.table-alerts .btn {
    @apply inline-block mr-1;
}
.table-alerts td,
.table-alerts th {
    @apply px-4 py-2 align-top;
}
.table-alerts-input {
    @apply w-full rounded-md py-1 px-2;
}
</style>
